import React, { createContext, useRef, useState } from 'react';
import PropTypes from 'prop-types';

// ── Web Storage availability detection ───────────────────────────────────────
// Detected once at module load so every call-site can use it synchronously.

function isStorageAvailable(type) {
    try {
        const storage = window[type];
        const testKey = '__nsd_cache_test__';
        storage.setItem(testKey, '1');
        storage.removeItem(testKey);
        return true;
    } catch {
        return false;
    }
}

const LOCAL_AVAILABLE =
    typeof window !== 'undefined' && isStorageAvailable('localStorage');
const SESSION_AVAILABLE =
    typeof window !== 'undefined' && isStorageAvailable('sessionStorage');

// Namespace prefix for all NSD cache entries in web storage. Ensures the
// full-clear path only removes OUR keys, not keys set by other libraries
// or browser extensions sharing the same storage origin.
const NSD_CACHE_PREFIX = 'nsd_cache:';

function resolveStorage(requested) {
    if (requested === 'local' && !LOCAL_AVAILABLE) return 'memory';
    if (requested === 'session' && !SESSION_AVAILABLE) return 'memory';
    return requested || 'memory';
}

function getWebStore(backend) {
    if (backend === 'local' && LOCAL_AVAILABLE) return localStorage;
    if (backend === 'session' && SESSION_AVAILABLE) return sessionStorage;
    return null;
}

function readFromWebStorage(cacheKey, store) {
    try {
        const raw = store.getItem(NSD_CACHE_PREFIX + cacheKey);
        if (!raw) return undefined;
        const { data, expiresAt } = JSON.parse(raw);
        // Expiry-only check: a falsy `data` (null, [], 0) is still a valid hit
        // when within TTL.
        if (Date.now() > expiresAt) {
            store.removeItem(NSD_CACHE_PREFIX + cacheKey);
            return undefined;
        }
        return data;
    } catch {
        return undefined;
    }
}

function writeToWebStorage(cacheKey, val, cacheTime, store) {
    try {
        store.setItem(
            NSD_CACHE_PREFIX + cacheKey,
            JSON.stringify({ data: val, expiresAt: Date.now() + cacheTime })
        );
    } catch {
        // Quota exceeded or blocked — silently fall back to memory cache.
    }
}

function removeFromWebStorage(cacheKey) {
    const storageKey = NSD_CACHE_PREFIX + cacheKey;
    if (LOCAL_AVAILABLE) {
        try {
            localStorage.removeItem(storageKey);
        } catch {
            /* ignore */
        }
    }
    if (SESSION_AVAILABLE) {
        try {
            sessionStorage.removeItem(storageKey);
        } catch {
            /* ignore */
        }
    }
}

// ── Context ──────────────────────────────────────────────────────────────────

const CacheContext = createContext({});

export const CacheProvider = ({ children }) => {
    const [cache, setCache] = useState({});

    // Ref mirror of cache state — read synchronously without re-render concerns.
    // Updated alongside setCache so consumers can safely read during render or
    // inside async callbacks where the React state would be stale.
    const cacheRef = useRef({});

    // setTimeout handles for TTL eviction, keyed by cacheKey. Lets us cancel
    // the previous timer when the same key is overwritten before it expires.
    const timersRef = useRef({});

    const removeFromCache = cacheKey => {
        delete cacheRef.current[cacheKey];
        setCache(prevCache => {
            const { [cacheKey]: _removed, ...rest } = prevCache;
            return rest;
        });
        removeFromWebStorage(cacheKey);
    };

    /**
     * Save a value to the cache.
     *
     * @param {object} key       Structured cache key (serialised to JSON internally).
     * @param {*}      val       Value to cache (must be JSON-serialisable for web storage).
     * @param {number} cacheTime TTL in milliseconds.
     * @param {string} [storage] Backend: 'memory' (default) | 'session' | 'local'.
     */
    const handleSaveToCache = (key, val, cacheTime, storage = 'memory') => {
        const cacheKey = JSON.stringify(key);
        cacheRef.current = { ...cacheRef.current, [cacheKey]: val };
        setCache(prevCache => ({ ...prevCache, [cacheKey]: val }));

        if (cacheTime) {
            // Cancel any existing timer for this key so a double-write does not
            // cause premature eviction of the freshly stored value.
            clearTimeout(timersRef.current[cacheKey]);
            timersRef.current[cacheKey] = setTimeout(() => {
                delete timersRef.current[cacheKey];
                removeFromCache(cacheKey);
            }, cacheTime);
        }

        const backend = resolveStorage(storage);
        const store = getWebStore(backend);
        if (store && cacheTime) {
            writeToWebStorage(cacheKey, val, cacheTime, store);
        }
    };

    /**
     * Retrieve a value from the cache. Falls back through sessionStorage →
     * localStorage on an in-memory miss so data persisted on a previous render
     * or page-load is still returned.
     *
     * @param {object} key Same structured key passed to saveToCache.
     * @returns {*} Cached value, or undefined on miss.
     */
    const handleGetFromCache = key => {
        const cacheKey = JSON.stringify(key);

        // Fast path: ref-backed in-memory lookup — safe to call from anywhere
        // (including render) because it reads a ref, not React state.
        if (cacheRef.current[cacheKey] !== undefined)
            return cacheRef.current[cacheKey];

        if (SESSION_AVAILABLE) {
            const val = readFromWebStorage(cacheKey, sessionStorage);
            if (val !== undefined) {
                cacheRef.current = { ...cacheRef.current, [cacheKey]: val };
                setCache(prev => ({ ...prev, [cacheKey]: val }));
                return val;
            }
        }

        if (LOCAL_AVAILABLE) {
            const val = readFromWebStorage(cacheKey, localStorage);
            if (val !== undefined) {
                cacheRef.current = { ...cacheRef.current, [cacheKey]: val };
                setCache(prev => ({ ...prev, [cacheKey]: val }));
                return val;
            }
        }

        return undefined;
    };

    /**
     * Remove one key (or all keys) from the cache.
     *
     * @param {object} [key] Key to remove. Omit to clear everything.
     */
    const handleClearCache = key => {
        if (typeof key === 'undefined') {
            cacheRef.current = {};
            setCache({});
            Object.values(timersRef.current).forEach(clearTimeout);
            timersRef.current = {};
            // Wipe ONLY namespaced entries from web storage — leave keys
            // belonging to other libraries or browser extensions untouched.
            for (const store of [
                SESSION_AVAILABLE ? sessionStorage : null,
                LOCAL_AVAILABLE ? localStorage : null,
            ]) {
                if (!store) continue;
                try {
                    const keysToRemove = [];
                    for (let i = 0; i < store.length; i++) {
                        const k = store.key(i);
                        if (k && k.startsWith(NSD_CACHE_PREFIX))
                            keysToRemove.push(k);
                    }
                    keysToRemove.forEach(k => store.removeItem(k));
                } catch {
                    /* ignore */
                }
            }
        } else {
            const cacheKey = JSON.stringify(key);
            // Cancel a pending TTL timer for the targeted key.
            if (timersRef.current[cacheKey]) {
                clearTimeout(timersRef.current[cacheKey]);
                delete timersRef.current[cacheKey];
            }
            removeFromCache(cacheKey);
        }
    };

    /**
     * Remove all cache entries whose JSON-serialised key starts with the given
     * string prefix. Useful for bulk invalidation after mutations without
     * needing to know every exact key.
     *
     * @param {string} prefix Key prefix matched against the raw JSON string.
     */
    const handleInvalidateByPrefix = prefix => {
        setCache(prevCache => {
            const next = { ...prevCache };
            Object.keys(next).forEach(k => {
                if (k.startsWith(prefix)) delete next[k];
            });
            return next;
        });

        Object.keys(cacheRef.current).forEach(k => {
            if (k.startsWith(prefix)) {
                delete cacheRef.current[k];
                if (timersRef.current[k]) {
                    clearTimeout(timersRef.current[k]);
                    delete timersRef.current[k];
                }
            }
        });

        for (const store of [
            SESSION_AVAILABLE ? sessionStorage : null,
            LOCAL_AVAILABLE ? localStorage : null,
        ]) {
            if (!store) continue;
            try {
                const storagePrefix = NSD_CACHE_PREFIX + prefix;
                const keysToRemove = [];
                for (let i = 0; i < store.length; i++) {
                    const k = store.key(i);
                    if (k && k.startsWith(storagePrefix)) keysToRemove.push(k);
                }
                keysToRemove.forEach(k => store.removeItem(k));
            } catch {
                /* ignore */
            }
        }
    };

    return (
        <CacheContext.Provider
            value={{
                cache,
                getFromCache: handleGetFromCache,
                saveToCache: handleSaveToCache,
                clearCache: handleClearCache,
                invalidateByPrefix: handleInvalidateByPrefix,
            }}
        >
            {children}
        </CacheContext.Provider>
    );
};

CacheProvider.propTypes = {
    children: PropTypes.node,
};

export default CacheContext;
